import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import type { User } from '@/models';
import {
  selectAuthError,
  selectAuthStatus,
  selectCurrentUser,
  selectIsAuthenticated,
  login as loginThunk,
  logout as logoutThunk,
  type LoginCredentials,
} from '@/features/auth';
import { useAppDispatch, useAppSelector } from './redux';

interface UseAuthResult {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => Promise<void>;
}

export function useAuth(): UseAuthResult {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectCurrentUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const status = useAppSelector(selectAuthStatus);
  const error = useAppSelector(selectAuthError);

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<boolean> => {
      const result = await dispatch(loginThunk(credentials));
      return loginThunk.fulfilled.match(result);
    },
    [dispatch],
  );

  const logout = useCallback(async (): Promise<void> => {
    await dispatch(logoutThunk());
    navigate('/login', { replace: true });
  }, [dispatch, navigate]);

  return { user, isAuthenticated, isLoading: status === 'loading', error, login, logout };
}
